import type { LengthRange } from "../lengthRange.gen";

interface OverlapEvent {
  depth: number;
  delta: number;
}

type DepthRange = { depthFrom: number; depthTo: number };

const isValidRange = ({ depthFrom, depthTo }: DepthRange): boolean =>
  Number.isFinite(depthFrom) && Number.isFinite(depthTo) && depthFrom < depthTo;

const clipToBounds = (
  range: DepthRange,
  bounds?: LengthRange,
): DepthRange | undefined => {
  if (!bounds) {
    return range;
  }

  const depthFrom = Math.max(range.depthFrom, bounds.depthFrom);
  const depthTo = Math.min(range.depthTo, bounds.depthTo);
  return depthFrom < depthTo ? { depthFrom, depthTo } : undefined;
};

/**
 * Sweep-line algorithm that computes depth ranges where a new interval may be
 * placed without exceeding `maxOverlapCount` concurrent overlaps.
 *
 * @param otherIntervals Intervals already placed (depth ranges to sweep over).
 * @param maxOverlapCount Maximum number of intervals allowed at any depth.
 * @param bounds Optional bounds to clip the result to; if omitted the full
 *               (-Infinity, Infinity) range is considered.
 * @param getDepthRange Accessor for the depth range of each interval item.
 *                      Defaults to identity (assumes items are already DepthRange).
 * @returns Allowed depth ranges where a new interval may be placed.
 */
export function computeAllowableOverlapRanges<
  T extends DepthRange = LengthRange,
>(
  otherIntervals: T[],
  maxOverlapCount: number,
  bounds?: LengthRange,
  getDepthRange: (item: T) => DepthRange = (item) => item,
): LengthRange[] {
  if (maxOverlapCount <= 0) {
    return [];
  }

  if (bounds && !(bounds.depthFrom < bounds.depthTo)) {
    return [];
  }

  const constrainedIntervals = otherIntervals
    .map((item) => getDepthRange(item))
    .filter(isValidRange)
    .map((interval) => clipToBounds(interval, bounds))
    .filter((interval): interval is DepthRange => interval !== undefined);

  if (constrainedIntervals.length === 0) {
    return [bounds ?? { depthFrom: -Infinity, depthTo: Infinity }];
  }

  const events: OverlapEvent[] = constrainedIntervals.flatMap(
    ({ depthFrom, depthTo }) => [
      { depth: depthFrom, delta: 1 },
      { depth: depthTo, delta: -1 },
    ],
  );

  events.sort((a, b) => a.depth - b.depth || a.delta - b.delta);

  const forbiddenRanges: LengthRange[] = [];
  let overlapCount = 0;
  let currentForbiddenStart: number | undefined;

  for (const event of events) {
    const previousCount = overlapCount;
    overlapCount += event.delta;

    if (previousCount < maxOverlapCount && overlapCount >= maxOverlapCount) {
      currentForbiddenStart = event.depth;
    } else if (
      previousCount >= maxOverlapCount &&
      overlapCount < maxOverlapCount &&
      currentForbiddenStart !== undefined
    ) {
      if (currentForbiddenStart < event.depth) {
        forbiddenRanges.push({
          depthFrom: currentForbiddenStart,
          depthTo: event.depth,
        });
      }
      currentForbiddenStart = undefined;
    }
  }

  if (forbiddenRanges.length === 0) {
    return [bounds ?? { depthFrom: -Infinity, depthTo: Infinity }];
  }

  const allowedRanges: LengthRange[] = [];
  const minimumDepth = bounds?.depthFrom ?? -Infinity;
  const maximumDepth = bounds?.depthTo ?? Infinity;
  let cursor = minimumDepth;

  for (const forbidden of forbiddenRanges) {
    const forbiddenStart = Math.max(forbidden.depthFrom, minimumDepth);
    const forbiddenEnd = Math.min(forbidden.depthTo, maximumDepth);

    if (cursor < forbiddenStart) {
      allowedRanges.push({ depthFrom: cursor, depthTo: forbiddenStart });
    }

    cursor = Math.max(cursor, forbiddenEnd);
    if (cursor >= maximumDepth) {
      break;
    }
  }

  if (cursor < maximumDepth) {
    allowedRanges.push({ depthFrom: cursor, depthTo: maximumDepth });
  }

  return allowedRanges;
}
